import { useAuth } from '../contexts/AuthContext';
import { createApiHeadersGetter } from '../utils/api-headers';
import { WEBHOOKS_URL } from '../config/api-config';

export interface WebhookSubscription {
  subscriptionId: string;
  targetUrl: string;
  secret: string;
  eventTypes: string[];
  description: string;
  isActive: boolean;
  createdBy: string;
  createdByEmail: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSubscriptionRequest {
  targetUrl: string;
  eventTypes: string[];
  description: string;
}

export interface UpdateSubscriptionRequest {
  targetUrl?: string;
  eventTypes?: string[];
  description?: string;
  isActive?: boolean;
}

export const WEBHOOK_EVENT_TYPES = [
  'playbook.created',
  'playbook.updated',
  'playbook.deleted',
  'playbook.published',
  'playbook.rolled_back',
] as const;

function extractErrorMessage(err: any, fallback: string): string {
  if (typeof err?.detail === 'string') return err.detail;
  if (Array.isArray(err?.detail)) {
    return err.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join('; ');
  }
  return err?.message || fallback;
}

export const useWebhooksApi = () => {
  const { getAuthToken } = useAuth();
  const getHeaders = createApiHeadersGetter(getAuthToken);

  const listSubscriptions = async (): Promise<WebhookSubscription[]> => {
    const headers = await getHeaders();
    const response = await fetch(`${WEBHOOKS_URL}/subscriptions`, {
      method: 'GET',
      headers,
    });
    if (!response.ok) throw new Error('Failed to fetch webhook subscriptions');
    return response.json();
  };

  const getSubscription = async (id: string): Promise<WebhookSubscription> => {
    const headers = await getHeaders();
    const response = await fetch(`${WEBHOOKS_URL}/subscriptions/${id}`, {
      method: 'GET',
      headers,
    });
    if (!response.ok) throw new Error('Failed to fetch webhook subscription');
    return response.json();
  };

  const createSubscription = async (
    data: CreateSubscriptionRequest
  ): Promise<WebhookSubscription> => {
    const headers = await getHeaders();
    const response = await fetch(`${WEBHOOKS_URL}/subscriptions`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(err, 'Failed to create subscription'));
    }
    return response.json();
  };

  const updateSubscription = async (
    id: string,
    data: UpdateSubscriptionRequest
  ): Promise<WebhookSubscription> => {
    const headers = await getHeaders();
    const response = await fetch(`${WEBHOOKS_URL}/subscriptions/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(err, 'Failed to update subscription'));
    }
    return response.json();
  };

  const deleteSubscription = async (id: string): Promise<void> => {
    const headers = await getHeaders();
    const response = await fetch(`${WEBHOOKS_URL}/subscriptions/${id}`, {
      method: 'DELETE',
      headers,
    });
    if (!response.ok) throw new Error('Failed to delete subscription');
  };

  const rotateSecret = async (
    id: string
  ): Promise<{ subscriptionId: string; secret: string }> => {
    const headers = await getHeaders();
    const response = await fetch(
      `${WEBHOOKS_URL}/subscriptions/${id}/rotate-secret`,
      { method: 'POST', headers }
    );
    if (!response.ok) throw new Error('Failed to rotate secret');
    return response.json();
  };

  const testSubscription = async (
    id: string
  ): Promise<{ message: string }> => {
    const headers = await getHeaders();
    const response = await fetch(`${WEBHOOKS_URL}/test/${id}`, {
      method: 'POST',
      headers,
    });
    if (!response.ok) throw new Error('Failed to send test ping');
    return response.json();
  };

  return {
    listSubscriptions,
    getSubscription,
    createSubscription,
    updateSubscription,
    deleteSubscription,
    rotateSecret,
    testSubscription,
  };
};
